import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, fmtErr } from "@/lib/auth";
import { toast } from "sonner";
import * as Icons from "lucide-react";

function Icon({ name, ...p }) { const C = Icons[name] || Icons.Circle; return <C {...p} />; }

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: isLogin ? "demo@mfdx.in" : "", password: isLogin ? "demo1234" : "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = new URLSearchParams(window.location.search).get("ref");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const user = isLogin ? await login(form.email, form.password) : await register(form.name, form.email, form.password, ref);
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      nav(user.role === "admin" ? "/app" : user.onboarded ? "/app" : "/onboarding");
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8E3D0]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 relative overflow-hidden">
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full grad-blue-violet opacity-30 blur-3xl" />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="w-9 h-9 rounded-xl grad-blue-violet grid place-items-center font-head font-bold">M</div>
          <span className="font-head font-bold text-xl">MFDX</span>
        </Link>
        <div className="relative">
          <h2 className="font-head text-4xl font-bold leading-tight">The AI Operating System for Mutual Fund Distributors.</h2>
          <p className="text-gray-400 mt-4 text-lg">Acquire clients, market yourself, stay compliant and grow your AUM — pay only for what you use.</p>
          <div className="mt-8 flex gap-3">
            {[["Sparkles", "AI Assistant"], ["ShieldCheck", "SEBI decoded"], ["Wallet", "Prepaid"]].map(([i, t]) => (
              <div key={t} className="bg-white/10 rounded-2xl px-4 py-3 text-sm flex items-center gap-2"><Icon name={i} size={16} /> {t}</div>
            ))}
          </div>
        </div>
        <span className="text-xs text-gray-500 relative">Demo prototype · illustrative data</span>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl grad-blue-violet grid place-items-center text-white font-head font-bold">M</div>
            <span className="font-head font-bold text-xl">MFDX</span>
          </div>
          <h1 className="font-head text-3xl font-bold">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-gray-500 mt-1">{isLogin ? "Log in to your MFD workspace." : "Start building your MFD business in minutes."}</p>

          {isLogin && <div className="mt-4 text-xs bg-blue-50 text-blue-700 rounded-xl px-3 py-2">Demo account prefilled · demo@mfdx.in / demo1234</div>}
          {!isLogin && ref && <div className="mt-4 text-xs bg-emerald-50 text-emerald-700 rounded-xl px-3 py-2" data-testid="referral-banner">🎉 You're joining via an invite — welcome to the MFD community!</div>}
          {err && <div className="mt-4 text-sm bg-red-50 text-red-600 rounded-xl px-3 py-2" data-testid="auth-error">{err}</div>}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {!isLogin && (
              <div>
                <label className="text-sm font-medium text-gray-700">Full name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name-input"
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" placeholder="Arjun Mehta" />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input"
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Password</label>
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password-input"
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" placeholder="••••••••" />
            </div>
            <button disabled={busy} type="submit" data-testid="auth-submit-btn"
              className="w-full py-3.5 rounded-xl grad-blue-violet text-white font-semibold hover-lift disabled:opacity-60 flex items-center justify-center gap-2">
              {busy ? "Please wait…" : isLogin ? "Log in" : "Create account"} <Icon name="ArrowRight" size={18} />
            </button>
          </form>
          <p className="mt-6 text-sm text-gray-500 text-center">
            {isLogin ? "New to MFDX? " : "Already have an account? "}
            <Link to={isLogin ? "/signup" : "/login"} className="text-blue-600 font-semibold">{isLogin ? "Start Free" : "Log in"}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
